/**
 * The account's actions as the device runs them against the relay (part 36c, tables PF, CH, FX, RQ, PD, DA; decisions
 * 24, 25, 53, 54; principles C9–C13): the fresh proof (P, then F once the account has it) and what each grant is spent
 * on, the links that confirm a change or an enrolment, turning F on, the reset by E and deleting the account. The
 * password goes only to the KDF worker (C3); a new password is derived under a new KDF record made here (C13). Every
 * request is signed by the device key for its purpose (CH5). Nothing here reads or writes a book (C14). Every effect is
 * injected, so it runs against the relay in tests.
 */
import { newKdfRecord } from '@cockatrice/crypto';
import type { KdfParameters } from '@cockatrice/crypto';
import { kdfJson, problemOf, secretOf, withProof } from './flows.ts';
import type { FlowDeps, Problem, SecondProof, Typed } from './flows.ts';
import type { Reply } from './client.ts';
import type { Email } from './email.ts';
import {
  readAddStarted,
  readAdvanced,
  readCancelled,
  readCheckEmail,
  readDeletionPending,
  readEnrolled,
  readGranted,
  readKdf,
  readNewCodes,
  readProofAnswer,
  readResetClaimed,
  readSetup,
} from './wire.ts';
import type { AccountId, Advanced, FactorSetup, Granted, SecondFactorNeeded } from './wire.ts';

/** A second factor's kind as the relay names it (EN1, FC4). */
export type FactorKind = 'totp' | 'passkey';

/** A factor a removal names (FC3): the app's, or a passkey by its credential ID. */
export type NamedFactor = { readonly kind: 'totp' } | { readonly kind: 'passkey'; readonly id: string };

/** The proof a new factor is confirmed with (EN10, EN12, FC6): a code from the app, or a passkey's registration. */
export type NewFactorProof = { readonly totp: string } | { readonly passkey: unknown };

/** A step's outcome: its value, or why not. */
export type Outcome<T> = { readonly kind: 'ok'; readonly value: T } | Problem;

function ok<T>(value: T): Outcome<T> {
  return { kind: 'ok', value };
}

/** A reply as an {@link Outcome}. */
function outcomeOf<T>(reply: Reply<T>): Outcome<T> {
  return reply.kind === 'ok' ? ok(reply.value) : problemOf(reply);
}

/**
 * PF1, PF2: the fresh proof's first step for `account` at `email` with `password` (C7's bounds, the highest kept for the
 * address): a grant (no F), or the F step to take with its ticket.
 */
export async function startProof(
  deps: FlowDeps,
  account: AccountId,
  email: Email,
  password: string,
  highest: KdfParameters | null,
): Promise<Outcome<Granted | SecondFactorNeeded>> {
  const outcome = await startTypedProof(deps, account, email, password, highest);
  if (outcome.kind !== 'ok') return outcome;
  outcome.value.typed.wrapping.fill(0);
  return ok(outcome.value.answer);
}

/** PF1, part 38c (GP3, KG9): {@link startProof}, with what the password gave (its wrapping key, for the keyring's rewrap). */
export async function startTypedProof(
  deps: FlowDeps,
  account: AccountId,
  email: Email,
  password: string,
  highest: KdfParameters | null,
): Promise<Outcome<{ readonly answer: Granted | SecondFactorNeeded; readonly typed: Typed }>> {
  const salt = await deps.client.post('/v1/login/salt', { email }, readKdf);
  if (salt.kind !== 'ok') return problemOf(salt);
  const derived = await secretOf(deps, password, salt.value, highest);
  if (!('secret' in derived)) return derived;
  const answer = outcomeOf(await withProof(deps, 'proof', (device) => deps.client.post('/v1/proof', { account, loginSecret: derived.secret, device }, readProofAnswer)));
  if (answer.kind !== 'ok') derived.typed.wrapping.fill(0);
  return answer.kind === 'ok' ? ok({ answer: answer.value, typed: derived.typed }) : answer;
}

/**
 * Part 38c (KG9, KW2): the keyring's wrap a change of P carries: under the new password's `typed`, from the keyring this
 * device holds or the relay's opened with the old password's; null when the account has no keyring; `failed` when it
 * can't be made (the change then isn't sent: the relay would refuse it, `keyring-required`).
 */
export type Rewrap = (account: AccountId, next: Typed) => Promise<{ readonly id: string; readonly wrapped: string } | null | 'failed'>;

/**
 * Part 38c (GP3, CL1): a grant from a login secret just derived (`Typed.secret`, while the password is at hand): a grant
 * when the account has no F, else the F step to take (the invite then waits for its Accept form).
 */
export async function proveWithSecret(deps: FlowDeps, account: AccountId, secret: string): Promise<Outcome<Granted | SecondFactorNeeded>> {
  return outcomeOf(await withProof(deps, 'proof', (device) => deps.client.post('/v1/proof', { account, loginSecret: secret, device }, readProofAnswer)));
}

/** PF2: the proof's F step with `ticket`: a grant holding F (the backup codes left when one was used). */
export async function proveSecondStep(deps: FlowDeps, ticket: string, proof: SecondProof): Promise<Outcome<Granted>> {
  return outcomeOf(await withProof(deps, 'proof', (device) => deps.client.post('/v1/proof/second-factor', { ticket, device, ...proof }, readGranted)));
}

/** `path` with `body`, the account, the grant and this device's `change` signature: what every change and cancel send (CG1). */
async function granted<T>(deps: FlowDeps, path: string, account: AccountId, grant: string, body: Record<string, unknown>, read: (value: unknown) => T | null): Promise<Outcome<T>> {
  return outcomeOf(await withProof(deps, 'change', (device) => deps.client.post(path, { account, grant, device, ...body }, read)));
}

/**
 * CH1, C13: a new password for `account`, derived under a new KDF record at `parameters` (the highest kept for the
 * address, so the record never lowers what this device accepts): how far the change got.
 */
export async function changePassword(deps: FlowDeps, account: AccountId, grant: string, password: string, parameters: KdfParameters, rewrap: Rewrap | null = null): Promise<Outcome<Advanced>> {
  const record = newKdfRecord((bytes) => deps.random(bytes), parameters);
  const derived = await secretOf(deps, password, record, null);
  if (!('secret' in derived)) return derived;
  let keyring: { readonly id: string; readonly wrapped: string } | null | 'failed';
  try {
    // KG9: the keyring's new wrap made before anything is sent, and the new wrapping key zeroed whatever happens.
    keyring = rewrap === null ? null : await rewrap(account, derived.typed);
  } finally {
    derived.typed.wrapping.fill(0);
  }
  if (keyring === 'failed') return { kind: 'failed' };
  return granted(deps, '/v1/account/password', account, grant, { kdf: kdfJson(record), loginSecret: derived.secret, ...(keyring === null ? {} : { keyring }) }, readAdvanced);
}

/** CH2: a new address for `account`. */
export function changeEmail(deps: FlowDeps, account: AccountId, grant: string, email: Email): Promise<Outcome<Advanced>> {
  return granted(deps, '/v1/account/email', account, grant, { email }, readAdvanced);
}

/** CH3: revokes `account`'s device `target` (its ID from the device list). */
export function revokeDevice(deps: FlowDeps, account: AccountId, grant: string, target: string): Promise<Outcome<Advanced>> {
  return granted(deps, '/v1/account/revoke', account, grant, { target }, readAdvanced);
}

/** CH4: a change's link, confirmed on this device (the one that started it). */
export async function confirmLink(deps: FlowDeps, account: AccountId, token: string): Promise<Outcome<Advanced>> {
  return outcomeOf(await withProof(deps, 'change', (device) => deps.client.post('/v1/account/confirm', { account, token, device }, readAdvanced)));
}

/** PD2, RQ4: cancels `account`'s open or pending change `change`. */
export function cancelChange(deps: FlowDeps, account: AccountId, grant: string, change: number): Promise<Outcome<true>> {
  return granted(deps, '/v1/account/cancel', account, grant, { change }, readCancelled);
}

/** FX2: starts turning F on for `account` with `password` typed (EN1): the link mailed, to open on this device. */
export async function startEnrolment(deps: FlowDeps, account: AccountId, email: Email, password: string, highest: KdfParameters | null, kind: FactorKind): Promise<Outcome<true>> {
  const salt = await deps.client.post('/v1/login/salt', { email }, readKdf);
  if (salt.kind !== 'ok') return problemOf(salt);
  const derived = await secretOf(deps, password, salt.value, highest);
  if (!('secret' in derived)) return derived;
  derived.typed.wrapping.fill(0);
  return outcomeOf(
    await withProof(deps, 'enrol', (device) => deps.client.post('/v1/second-factor/start', { account, kind, loginSecret: derived.secret, device }, (value) => (readCheckEmail(value) ? true : null))),
  );
}

/** FX3: the enrolment's setup from its link's `token`: the app's seed or the passkey's prompt. */
export async function setUpEnrolment(deps: FlowDeps, account: AccountId, token: string): Promise<Outcome<FactorSetup>> {
  return outcomeOf(await withProof(deps, 'enrol', (device) => deps.client.post('/v1/second-factor/setup', { account, token, device }, readSetup)));
}

/** FX4: the enrolment confirmed with `proof`: F on, and its backup codes, shown once (C15). */
export async function confirmEnrolment(deps: FlowDeps, account: AccountId, token: string, proof: NewFactorProof): Promise<Outcome<readonly string[]>> {
  return outcomeOf(await withProof(deps, 'enrol', (device) => deps.client.post('/v1/second-factor/confirm', { account, token, device, ...proof }, readEnrolled)));
}

/** FX7: removes `factor` from `account`. */
export function removeFactor(deps: FlowDeps, account: AccountId, grant: string, factor: NamedFactor): Promise<Outcome<Advanced>> {
  return granted(deps, '/v1/account/second-factor/remove', account, grant, { factor }, readAdvanced);
}

/** FX6: starts adding a factor of `kind` to `account`: the change's ID and its setup. */
export function addFactor(deps: FlowDeps, account: AccountId, grant: string, kind: FactorKind): Promise<Outcome<{ readonly change: number; readonly setup: FactorSetup }>> {
  return granted(deps, '/v1/account/second-factor/add', account, grant, { kind }, readAddStarted);
}

/** FX6: an add's change `change` proved with the new factor, on the device that started it. */
export async function confirmAdd(deps: FlowDeps, account: AccountId, change: number, proof: NewFactorProof): Promise<Outcome<Advanced>> {
  return outcomeOf(await withProof(deps, 'change', (device) => deps.client.post('/v1/account/second-factor/confirm', { account, change, device, ...proof }, readAdvanced)));
}

/** FX8: a new set of backup codes for `account`, shown once (C15), and how far its change got. */
export function newBackupCodes(deps: FlowDeps, account: AccountId, grant: string): Promise<Outcome<{ readonly outcome: Advanced; readonly codes: readonly string[] }>> {
  return granted(deps, '/v1/account/backup-codes', account, grant, {}, readNewCodes);
}

/** RQ1: asks a reset for `email`: "check your email", the same for every address (RS2). */
export async function requestReset(deps: FlowDeps, email: Email): Promise<Outcome<true>> {
  return outcomeOf(await deps.client.post('/v1/reset', { email }, (value) => (readCheckEmail(value) ? true : null)));
}

/**
 * RQ2, C13: claims the reset whose link holds `token` with a new `password` (under a new KDF record at `parameters`),
 * removing F when asked (N3): the reset pending, its account and time.
 */
export async function claimReset(
  deps: FlowDeps,
  token: string,
  password: string,
  removeFactor: boolean,
  parameters: KdfParameters,
): Promise<Outcome<{ readonly account: AccountId; readonly change: number; readonly dueAt: number }>> {
  const record = newKdfRecord((bytes) => deps.random(bytes), parameters);
  const derived = await secretOf(deps, password, record, null);
  if (!('secret' in derived)) return derived;
  // GP3: a reset's new P unwraps nothing (KW4): its wrapping key is never used.
  derived.typed.wrapping.fill(0);
  return outcomeOf(
    await withProof(deps, 'reset', (device) => deps.client.post('/v1/reset/claim', { token, device, kdf: kdfJson(record), loginSecret: derived.secret, removeFactor }, readResetClaimed)),
  );
}

/** DA1: asks for `account`'s deletion: pending, with its time. */
export function askDeletion(deps: FlowDeps, account: AccountId, grant: string): Promise<Outcome<{ readonly dueAt: number }>> {
  return granted(deps, '/v1/account/delete', account, grant, {}, readDeletionPending);
}

/** DA2: cancels `account`'s pending deletion. */
export function cancelDeletion(deps: FlowDeps, account: AccountId, grant: string): Promise<Outcome<true>> {
  return granted(deps, '/v1/account/delete/cancel', account, grant, {}, readCancelled);
}

/** DA3: cancels the deletion whose mailed link holds `token`; no device or account needed (DE5). */
export async function cancelDeletionByLink(deps: FlowDeps, token: string): Promise<Outcome<true>> {
  return outcomeOf(await deps.client.post('/v1/delete/cancel', { token }, readCancelled));
}
